import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { createPortal } from 'react-dom'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  position: relative;
`

const Label = styled.label`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.muted};
`

const Trigger = styled.div`
  width: 100%;
  border: 1px solid ${({ theme, $isOpen }) => $isOpen ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  height: 40px;
  padding: 0 12px;
  background: white;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'text'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  box-shadow: ${({ theme, $isOpen }) => $isOpen ? `0 0 0 3px ${theme.colors.primary}33` : 'none'};
  transition: all 0.2s ease;
`

const Input = styled.input`
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;

  &::placeholder {
    color: ${({ theme }) => theme.colors.muted};
  }
`

const OptionsList = styled.div`
  position: fixed;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  max-height: 240px;
  overflow-y: auto;
  z-index: 99999;
  padding: 4px;
`

const Option = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radii.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ $isSelected, theme }) => $isSelected ? theme.colors.background : 'transparent'};

  &:hover {
    background: #f1f5f9;
  }
`

const Arrow = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.muted};
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease;
  pointer-events: none;
`

export function SelectWithCustom({ label, value, onChange, options = [], disabled, placeholder = 'Select or type...' }) {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const optionsRef = useRef(null)
  const inputRef = useRef(null)

  // Resolve display value: show label if value matches an option, otherwise show raw value
  const getDisplayValue = (val) => {
    if (!val) return ''
    const match = options.find(opt => {
      const optVal = typeof opt === 'object' ? opt.value : opt
      return optVal === val
    })
    if (match) return typeof match === 'object' ? match.label : match
    return val
  }

  useEffect(() => {
    setInputValue(getDisplayValue(value))
  }, [value, options])

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current && !containerRef.current.contains(event.target) &&
        (!optionsRef.current || !optionsRef.current.contains(event.target))
      ) {
        setIsOpen(false)
        setIsTyping(false)
        // Reset display to current value label
        setInputValue(getDisplayValue(value))
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value, options])

  useEffect(() => {
    if (!isOpen) return
    function handleResize() { setIsOpen(false) }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  const updateDropdownPos = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const dropdownMaxHeight = 248
    const openAbove = spaceBelow < dropdownMaxHeight && rect.top > spaceBelow
    if (openAbove) {
      setDropdownPos({ bottom: window.innerHeight - rect.top + 4, top: 'auto', left: rect.left, width: rect.width, openAbove: true })
    } else {
      setDropdownPos({ top: rect.bottom + 4, bottom: 'auto', left: rect.left, width: rect.width, openAbove: false })
    }
  }

  const handleInputChange = (e) => {
    if (disabled) return
    const newVal = e.target.value
    setInputValue(newVal)
    setIsTyping(true)
    if (!isOpen) {
      updateDropdownPos()
      setIsOpen(true)
    }
  }

  const handleOptionSelect = (opt) => {
    if (disabled) return
    const val = typeof opt === 'object' ? opt.value : opt
    onChange && onChange(val)
    setInputValue(getDisplayValue(val))
    setIsOpen(false)
    setIsTyping(false)
  }

  const handleCustomConfirm = () => {
    if (disabled || !inputValue.trim()) return
    onChange && onChange(inputValue.trim())
    setIsOpen(false)
    setIsTyping(false)
  }

  const handleTriggerClick = () => {
    if (disabled) return
    if (!isOpen) {
      updateDropdownPos()
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
    setIsTyping(false)
    if (!isOpen && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCustomConfirm()
    }
  }

  const filteredOptions = isTyping && inputValue
    ? options.filter(opt => {
        const lbl = typeof opt === 'object' ? opt.label : opt
        return lbl.toLowerCase().includes(inputValue.toLowerCase())
      })
    : options

  const isCustomValue = inputValue.trim() && !options.some(opt => {
    const lbl = typeof opt === 'object' ? opt.label : opt
    return lbl.toLowerCase() === inputValue.trim().toLowerCase()
  })

  return (
    <Wrapper ref={containerRef}>
      {label && <Label>{label}</Label>}
      <Trigger ref={triggerRef} $isOpen={isOpen} $disabled={disabled} onClick={handleTriggerClick}>
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        <Arrow $isOpen={isOpen}>▼</Arrow>
      </Trigger>

      {isOpen && createPortal(
        <OptionsList ref={optionsRef} style={{
          ...(dropdownPos.openAbove
            ? { bottom: dropdownPos.bottom, top: 'auto' }
            : { top: dropdownPos.top, bottom: 'auto' }),
          left: dropdownPos.left,
          width: dropdownPos.width,
        }}>
          {filteredOptions.map((opt, i) => {
            const val = typeof opt === 'object' ? opt.value : opt
            const labelStr = typeof opt === 'object' ? opt.label : opt
            return (
              <Option
                key={i}
                $isSelected={val === value}
                onClick={(e) => {
                  e.stopPropagation()
                  handleOptionSelect(opt)
                }}
              >
                {labelStr}
              </Option>
            )
          })}
          {isCustomValue && (
            <Option
              style={{ borderTop: filteredOptions.length > 0 ? '1px solid #f1f5f9' : 'none', color: '#6366f1', fontWeight: 500 }}
              onClick={(e) => {
                e.stopPropagation()
                handleCustomConfirm()
              }}
            >
              Usa "{inputValue.trim()}"
            </Option>
          )}
          {filteredOptions.length === 0 && !isCustomValue && (
            <Option style={{ fontStyle: 'italic', color: '#999', cursor: 'default' }}>
              Nessun risultato
            </Option>
          )}
        </OptionsList>,
        document.body
      )}
    </Wrapper>
  )
}
